import { Injectable, NgZone, inject } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import {
  IGeoJSONCircle,
  IUbicacion,
  IRecorrido,
  IRecorridoDinamico,
  Stop,
  IEventoGenerico,
  IValoresEventoTracker,
} from 'modelos/src';
import {
  LineStringMapaVehiculos,
  MapboxMapInstance,
  PointLineStringVehiculo,
  SeleccionadosBarraLateral,
} from '../../interfaces';
import {
  COLOR_REPORTE,
  colorDeVelocidad,
  rangoVelocidad,
} from '../../colores-viaje';
import {
  ubicarReportesEnTraza,
  valorInterpolado,
} from '../../traza-reportes.util';
import {
  colorComplementario,
  colorDelEspectro,
  colorVivo,
  mezclarColores,
} from '../../colores.util';

/**
 * Metros que se corre la traza del viaje hacia un costado, perpendicular a su
 * dirección de avance. Ida y vuelta por la misma calle van en sentidos
 * opuestos, así que cada una cae de su lado en vez de taparse.
 *
 * Se desplaza la GEOMETRÍA y no el pintado (`line-offset`): las flechas de
 * dirección son una capa de símbolos montada sobre esta misma geometría, con lo
 * cual acompañan solas. Con `line-offset` había que replicar el corrimiento en
 * `icon-offset`, que usa otra escala y otros ejes, y quedaban despegadas.
 *
 * Al ser metros, la separación se mantiene proporcional a la calle en cualquier
 * zoom.
 */
const METROS_DESPLAZAMIENTO_TRAZA = 2;

/** Metros por grado de latitud, para pasar el desplazamiento a coordenadas. */
const METROS_POR_GRADO = 111320;

/**
 * Largo del bloque de color y del hueco entre bloques, en multiplos del ancho
 * de la linea.
 */
const LARGO_BLOQUE_FLUJO = 8;
const LARGO_HUECO_FLUJO = 10.5;

/**
 * En cuantos pasos se divide un ciclo del movimiento. `line-dasharray` no se
 * interpola: la animacion es cambiar de patron, asi que los pasos son los
 * cuadros. Pocos pasos = movimiento a los saltos por mas que dure lo mismo.
 */
const PASOS_FLUJO = 60;

/** Cuanto tarda el bloque en avanzar un ciclo entero. */
const MS_CICLO_FLUJO = 3000;

/**
 * Patrones de guiones del movimiento, uno por paso. Cada uno corre el bloque un
 * poco mas adelante: el primer valor es lo que sobra del bloque anterior, y
 * cuando ese bloque se termina de ir, lo que crece es el hueco de adelante.
 */
const PATRONES_FLUJO: number[][] = Array.from(
  { length: PASOS_FLUJO },
  (_, paso) => {
    const avance =
      (paso / PASOS_FLUJO) * (LARGO_BLOQUE_FLUJO + LARGO_HUECO_FLUJO);

    return avance <= LARGO_BLOQUE_FLUJO
      ? [avance, LARGO_HUECO_FLUJO, LARGO_BLOQUE_FLUJO - avance]
      : [
          0,
          avance - LARGO_BLOQUE_FLUJO,
          LARGO_BLOQUE_FLUJO,
          LARGO_HUECO_FLUJO - (avance - LARGO_BLOQUE_FLUJO),
        ];
  },
);

/** Cuanto dura cada patron. Sale del ciclo y de la cantidad de pasos. */
const MS_POR_PASO_FLUJO = MS_CICLO_FLUJO / PASOS_FLUJO;

/**
 * Guión y hueco de la traza punteada de cerca, en anchos de línea. De lejos se
 * usa una versión más tupida (ver `ajustarPunteadoPorZoom`).
 */
const PUNTEADO_BASE = [1, 1.5];

/**
 * Tope de pedazos en los que se parte la traza con degradado. Más pedazos =
 * transición más suave y mejor resolución para detectar solapamientos, pero
 * más features que dibujar.
 */
const MAX_PEDAZOS_DEGRADADO = 400;

/** Largo mínimo de cada pedazo: por debajo de esto no aporta nada. */
const METROS_MINIMOS_POR_PEDAZO = 20;

/**
 * Traza por velocidad: largo de cada paso del degradado. Un segmento más largo
 * se subdivide para que el color cambie dentro de él y no de golpe en su
 * vértice. En viajes largos el paso crece para no pasar de `MAX_PASOS`.
 */
const METROS_MINIMOS_PASO_VELOCIDAD = 15;
const MAX_PASOS_VELOCIDAD = 1500;

/**
 * Escalones de color de la escala relativa por velocidad: a simple vista es un
 * degradado continuo, y los pedazos seguidos del mismo escalón se juntan.
 */
const ESCALONES_VELOCIDAD = 64;

/** A qué distancia dos pedazos cuentan como "encima" uno del otro. */
const METROS_CERCANIA_PEDAZOS = 35;

/**
 * Pedazos contiguos que no se cuentan como vecinos: son el anterior y el
 * siguiente de la misma pasada, no un solapamiento.
 */
const PEDAZOS_VECINOS_IGNORADOS = 2;

/** Tope de vecinos por pedazo, para que un estacionamiento no se coma todo. */
const MAX_VECINOS_CONTADOS = 8;

/**
 * Lo que consume un pedazo sin vecinos. Bajo pero no cero: en un tramo largo y
 * aislado el color deriva apenas, en vez de quedar completamente plano.
 */
const PESO_PEDAZO_AISLADO = 0.15;
import { ServiceGeneralVehiculos } from './service-general';
import { featuresCumplimientoRecorrido } from './cumplimiento-recorrido.util';

/**
 * Callbacks que el componente provee al servicio para interactuar con
 * estado que solo el componente conoce (outputs, selección, etc.)
 */
export interface DibujarCallbacks {
  isDibujoPermitido: () => boolean;
  emitCirculoBusquedaChange: (circle: IGeoJSONCircle | undefined) => void;
  getSeleccionadoBarraLateral: () => SeleccionadosBarraLateral | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceDibujarVehiculos {
  private ngZone = inject(NgZone);
  private serviceGeneral = inject(ServiceGeneralVehiculos);

  // ════════════════════════════════════════════════════════════════════════════
  // GEOMETRÍA
  // ════════════════════════════════════════════════════════════════════════════

  /** Fórmula de Haversine para calcular distancia en metros */
  public calculateDistance(
    coord1: [number, number],
    coord2: [number, number],
  ): number {
    const R = 6371000;
    const lat1 = (coord1[1] * Math.PI) / 180;
    const lat2 = (coord2[1] * Math.PI) / 180;
    const deltaLat = ((coord2[1] - coord1[1]) * Math.PI) / 180;
    const deltaLng = ((coord2[0] - coord1[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CÍRCULOS
  // ════════════════════════════════════════════════════════════════════════════

  /** Dibuja un círculo aproximado (polígono de 64 puntos) en el drawSrc */
  public updateCircleOnMap(
    instance: MapboxMapInstance,
    center: [number, number],
    radius: number,
  ) {
    const points = 64;
    const coords: [number, number][] = [];
    const latRad = (center[1] * Math.PI) / 180;
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dx = ((radius / 1000) * Math.cos(angle)) / (111 * Math.cos(latRad));
      const dy = ((radius / 1000) * Math.sin(angle)) / 111;
      coords.push([center[0] + dx, center[1] + dy]);
    }
    coords.push(coords[0]);

    const source = instance.map.getSource(
      instance.drawSrc,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [coords] },
            properties: {},
          },
        ],
      });
    }
  }

  /** Ajusta el mapa para que el círculo completo sea visible */
  public fitBoundsToCircle(
    instance: MapboxMapInstance,
    center: [number, number],
    radius: number,
  ) {
    const radiusKm = radius / 1000;
    const latRad = (center[1] * Math.PI) / 180;
    const lngOffset = radiusKm / (111 * Math.cos(latRad));
    const latOffset = radiusKm / 111;
    const bounds = new mapboxgl.LngLatBounds(
      [center[0] - lngOffset, center[1] - latOffset],
      [center[0] + lngOffset, center[1] + latOffset],
    );
    instance.map.fitBounds(bounds, { padding: 50, duration: 900 });
  }

  /** Redibuja el polígono de ubicación persistido en la instancia */
  public redrawPersistedPolygon(
    instance: MapboxMapInstance,
    defaultColor: string,
  ): void {
    if (!instance.polygonUbicacion?.geojson) return;
    const source = instance.map.getSource(
      instance.drawSrc,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        properties: {
          color: instance.polygonUbicacion.color || defaultColor,
        },
        geometry: instance.polygonUbicacion.geojson as GeoJSON.Geometry,
      };
      source.setData({ type: 'FeatureCollection', features: [feature] });
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // MODOS DE DIBUJO / EDICIÓN DE CÍRCULOS
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * Modo creación: click para colocar centro → mover para elegir radio → click para confirmar.
   * Después de confirmar, transiciona automáticamente al modo edición (resize/drag).
   */
  public enableCircleDraw(
    instance: MapboxMapInstance,
    callbacks: DibujarCallbacks,
  ) {
    let center: [number, number] | null = null;
    let lastPreviewRadius = 0;
    let phase: 'placingCenter' | 'choosingRadius' = 'placingCenter';

    const cleanupDraw = () => {
      instance.map.off('click', onClick);
      instance.map.off('mousemove', onMouseMove);
      instance.map.getCanvas().removeEventListener('keydown', onKeyDown);
      instance.map.getCanvas().style.cursor = '';
      instance.map.dragPan.enable();

      // Si se cierra durante la elección de radio y hay un preview válido,
      // confirmar el círculo con el último radio previsualizado
      // (caso: click en botón "Terminar edición" que se solapa con el mapa)
      if (phase === 'choosingRadius' && center && lastPreviewRadius > 10) {
        instance.circle = {
          type: 'Point',
          coordinates: center,
          radius: lastPreviewRadius,
        };
        this.ngZone.run(() => {
          callbacks.emitCirculoBusquedaChange(instance.circle);
        });
      }

      instance.dibujando = false;
    };

    const onClick = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido()) {
        cleanupDraw();
        return;
      }

      if (phase === 'placingCenter') {
        // Primer click: fijar el centro
        center = [e.lngLat.lng, e.lngLat.lat];
        phase = 'choosingRadius';
        instance.map.dragPan.disable();

        // Dibujar un punto inicial con radio mínimo como feedback visual
        this.updateCircleOnMap(instance, center, 10);
        instance.map.getCanvas().style.cursor = 'crosshair'; // Mira mientras se elige radio
      } else if (phase === 'choosingRadius' && center) {
        // Segundo click: confirmar el radio
        const current: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        let radius = this.calculateDistance(center, current);

        // Radio mínimo proporcional al zoom
        if (radius < 10) {
          const zoom = instance.map.getZoom();
          radius = Math.round(40_000_000 / Math.pow(2, zoom));
          radius = Math.max(10, Math.min(50000, radius));
        }

        instance.circle = {
          type: 'Point',
          coordinates: center,
          radius,
        };

        this.updateCircleOnMap(instance, center, radius);

        this.ngZone.run(() => {
          callbacks.emitCirculoBusquedaChange(instance.circle);
        });

        // Limpiar eventos de creación
        instance.map.off('click', onClick);
        instance.map.off('mousemove', onMouseMove);
        instance.map.getCanvas().removeEventListener('keydown', onKeyDown);
        instance.map.getCanvas().style.cursor = '';
        instance.map.dragPan.enable();

        // Transicionar al modo edición persistente
        instance._editCleanup = undefined;
        this.enablePersistentEdgeResize(instance, callbacks);
        this.enableCircleEdit(instance, callbacks);
      }
    };

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido()) {
        cleanupDraw();
        return;
      }

      // Solo mostrar preview del radio después de fijar el centro
      if (phase !== 'choosingRadius' || !center) return;

      const current: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const radius = this.calculateDistance(center, current);
      lastPreviewRadius = Math.max(radius, 10);
      this.updateCircleOnMap(instance, center, lastPreviewRadius);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Si estamos eligiendo radio, volver a elegir centro
        if (phase === 'choosingRadius') {
          phase = 'placingCenter';
          center = null;
          instance.map.dragPan.enable();
          // Limpiar preview
          const source = instance.map.getSource(
            instance.drawSrc,
          ) as mapboxgl.GeoJSONSource;
          if (source) {
            source.setData({ type: 'FeatureCollection', features: [] });
          }
          instance.map.getCanvas().style.cursor = 'pointer';
        } else {
          cleanupDraw();
        }
      }
    };

    // Guardar cleanup
    instance._editCleanup = cleanupDraw;

    this.ngZone.runOutsideAngular(() => {
      instance.map.on('click', onClick);
      instance.map.on('mousemove', onMouseMove);
      instance.map.getCanvas().addEventListener('keydown', onKeyDown);
    });

    instance.map.getCanvas().style.cursor = 'pointer';
  }

  /** Modo edición: resize borde + drag centro de un círculo existente */
  public enableCircleEdit(
    instance: MapboxMapInstance,
    callbacks: DibujarCallbacks,
  ) {
    if (!instance.circle?.coordinates || !instance.circle.radius) {
      this.enableCircleDraw(instance, callbacks);
      return;
    }

    const getCenter = (): [number, number] =>
      instance.circle?.coordinates as [number, number];
    const getRadius = (): number => instance.circle?.radius || 100;

    let draggingEdge = false;
    let draggingCenter = false;
    let lastMousePos: [number, number] | null = null;

    const isNearEdge = (lngLat: mapboxgl.LngLat): boolean => {
      const center = getCenter();
      if (!center) return false;
      const dist = this.calculateDistance(center, [lngLat.lng, lngLat.lat]);
      const radius = getRadius();
      const tolerance = Math.max(50, Math.min(5000, radius * 0.1));
      return Math.abs(dist - radius) < tolerance;
    };

    const isInsideCircle = (lngLat: mapboxgl.LngLat): boolean => {
      const center = getCenter();
      if (!center) return false;
      const dist = this.calculateDistance(center, [lngLat.lng, lngLat.lat]);
      const radius = getRadius();
      const tolerance = Math.max(50, Math.min(5000, radius * 0.1));
      return dist < radius - tolerance;
    };

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido()) {
        cleanup();
        return;
      }

      const center = getCenter();
      if (!center) return;

      if (draggingEdge) {
        let newRadius = this.calculateDistance(center, [
          e.lngLat.lng,
          e.lngLat.lat,
        ]);
        if (newRadius < 10) newRadius = 10;
        instance.circle = { ...instance.circle, radius: newRadius };
        this.updateCircleOnMap(instance, center, newRadius);
        return;
      }

      if (draggingCenter) {
        if (!lastMousePos) return;
        const deltaLng = e.lngLat.lng - lastMousePos[0];
        const deltaLat = e.lngLat.lat - lastMousePos[1];
        const newCenter: [number, number] = [
          center[0] + deltaLng,
          center[1] + deltaLat,
        ];
        instance.circle = { ...instance.circle, coordinates: newCenter };
        this.updateCircleOnMap(instance, newCenter, getRadius());
        lastMousePos = [e.lngLat.lng, e.lngLat.lat];
        return;
      }

      if (isNearEdge(e.lngLat)) {
        instance.map.getCanvas().style.cursor = 'nesw-resize';
      } else if (isInsideCircle(e.lngLat)) {
        instance.map.getCanvas().style.cursor = 'move';
      } else {
        instance.map.getCanvas().style.cursor = 'default';
      }
    };

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido()) {
        cleanup();
        return;
      }

      if (isNearEdge(e.lngLat)) {
        draggingEdge = true;
        instance.map.dragPan.disable();
        e.preventDefault();
      } else if (isInsideCircle(e.lngLat)) {
        draggingCenter = true;
        lastMousePos = [e.lngLat.lng, e.lngLat.lat];
        instance.map.dragPan.disable();
        instance.map.getCanvas().style.cursor = 'move';
        e.preventDefault();
      }
    };

    const onMouseUp = () => {
      if (!draggingEdge && !draggingCenter) return;
      draggingEdge = false;
      draggingCenter = false;
      lastMousePos = null;
      instance.map.dragPan.enable();

      this.ngZone.run(() => {
        callbacks.emitCirculoBusquedaChange(instance.circle);
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
      }
    };

    const cleanup = () => {
      // Si estábamos arrastrando, emitir el estado actual del círculo
      // (caso: mouseup cae en el botón "Terminar edición" solapado)
      if (draggingEdge || draggingCenter) {
        draggingEdge = false;
        draggingCenter = false;
        lastMousePos = null;
        this.ngZone.run(() => {
          callbacks.emitCirculoBusquedaChange(instance.circle);
        });
      }

      instance.map.off('mousemove', onMouseMove);
      instance.map.off('mousedown', onMouseDown);
      instance.map.off('mouseup', onMouseUp);
      instance.map.getCanvas().removeEventListener('keydown', onKeyDown);
      instance.map.getCanvas().style.cursor = '';
      instance.map.dragPan.enable();
      instance.dibujando = false;
    };

    instance._editCleanup = cleanup;

    this.ngZone.runOutsideAngular(() => {
      instance.map.on('mousemove', onMouseMove);
      instance.map.on('mousedown', onMouseDown);
      instance.map.on('mouseup', onMouseUp);
      instance.map.getCanvas().addEventListener('keydown', onKeyDown);
    });

    instance.map.getCanvas().style.cursor = 'default';
  }

  /** Habilita resize/drag persistente (fuera de modo dibujo activo) */
  public enablePersistentEdgeResize(
    instance: MapboxMapInstance,
    callbacks: DibujarCallbacks,
  ) {
    this.disablePersistentEdgeResize(instance);

    if (!instance.circle?.coordinates || !instance.circle.radius) return;

    let draggingEdge = false;
    let draggingCenter = false;
    let lastMousePos: [number, number] | null = null;

    const getCenter = (): [number, number] =>
      instance.circle?.coordinates as [number, number];
    const getRadius = (): number => instance.circle?.radius || 100;

    const isNearEdge = (lngLat: mapboxgl.LngLat): boolean => {
      const center = getCenter();
      if (!center) return false;
      const dist = this.calculateDistance(center, [lngLat.lng, lngLat.lat]);
      const radius = getRadius();
      const tolerance = Math.max(50, Math.min(5000, radius * 0.1));
      return Math.abs(dist - radius) < tolerance;
    };

    const isInsideCircle = (lngLat: mapboxgl.LngLat): boolean => {
      const center = getCenter();
      if (!center) return false;
      const dist = this.calculateDistance(center, [lngLat.lng, lngLat.lat]);
      const radius = getRadius();
      const tolerance = Math.max(50, Math.min(5000, radius * 0.1));
      return dist < radius - tolerance;
    };

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido() || instance.dibujando) {
        const cursor = instance.map.getCanvas().style.cursor;
        if (cursor === 'nesw-resize' || cursor === 'move') {
          instance.map.getCanvas().style.cursor = '';
        }
        return;
      }

      if (draggingEdge) {
        const center = getCenter();
        if (!center) return;
        let newRadius = this.calculateDistance(center, [
          e.lngLat.lng,
          e.lngLat.lat,
        ]);
        if (newRadius < 10) newRadius = 10;
        instance.circle = { ...instance.circle, radius: newRadius };
        this.updateCircleOnMap(instance, center, newRadius);
        return;
      }

      if (draggingCenter) {
        const center = getCenter();
        if (!center || !lastMousePos) return;
        const deltaLng = e.lngLat.lng - lastMousePos[0];
        const deltaLat = e.lngLat.lat - lastMousePos[1];
        const newCenter: [number, number] = [
          center[0] + deltaLng,
          center[1] + deltaLat,
        ];
        instance.circle = { ...instance.circle, coordinates: newCenter };
        this.updateCircleOnMap(instance, newCenter, getRadius());
        lastMousePos = [e.lngLat.lng, e.lngLat.lat];
        return;
      }

      if (isNearEdge(e.lngLat)) {
        instance.map.getCanvas().style.cursor = 'nesw-resize';
      } else if (isInsideCircle(e.lngLat)) {
        instance.map.getCanvas().style.cursor = 'move';
      } else if (
        instance.map.getCanvas().style.cursor === 'nesw-resize' ||
        instance.map.getCanvas().style.cursor === 'move'
      ) {
        instance.map.getCanvas().style.cursor = '';
      }
    };

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (!callbacks.isDibujoPermitido() || instance.dibujando) return;

      if (isNearEdge(e.lngLat)) {
        draggingEdge = true;
        instance.map.dragPan.disable();
        e.preventDefault();
      } else if (isInsideCircle(e.lngLat)) {
        draggingCenter = true;
        lastMousePos = [e.lngLat.lng, e.lngLat.lat];
        instance.map.dragPan.disable();
        instance.map.getCanvas().style.cursor = 'move';
        e.preventDefault();
      }
    };

    const onMouseUp = () => {
      if (!draggingEdge && !draggingCenter) return;

      draggingEdge = false;
      draggingCenter = false;
      lastMousePos = null;
      instance.map.dragPan.enable();
      instance.map.getCanvas().style.cursor = '';

      this.ngZone.run(() => {
        callbacks.emitCirculoBusquedaChange(instance.circle);
      });
    };

    this.ngZone.runOutsideAngular(() => {
      instance.map.on('mousemove', onMouseMove);
      instance.map.on('mousedown', onMouseDown);
      instance.map.on('mouseup', onMouseUp);
    });

    instance._persistentEdgeHandlers = {
      onMouseMove,
      onMouseDown,
      onMouseUp,
      isDragging: () => draggingEdge || draggingCenter,
      finalizeDrag: () => {
        if (!draggingEdge && !draggingCenter) return;
        draggingEdge = false;
        draggingCenter = false;
        lastMousePos = null;
        instance.map.dragPan.enable();
        this.ngZone.run(() => {
          callbacks.emitCirculoBusquedaChange(instance.circle);
        });
      },
    };
  }

  /** Limpia listeners de edición persistente */
  public disablePersistentEdgeResize(instance: MapboxMapInstance) {
    const handlers = instance._persistentEdgeHandlers;
    if (!handlers) return;

    // Si estaba arrastrando, emitir el estado actual antes de limpiar
    handlers.finalizeDrag?.();

    instance.map.off('mousemove', handlers.onMouseMove);
    instance.map.off('mousedown', handlers.onMouseDown);
    instance.map.off('mouseup', handlers.onMouseUp);
    instance._persistentEdgeHandlers = undefined;
  }

  /** Desactiva completamente el modo edición/dibujo de una instancia */
  public desactivarModoEdicionParaInstancia(instance: MapboxMapInstance) {
    if (instance._editCleanup) {
      instance._editCleanup();
      instance._editCleanup = undefined;
    }

    this.disablePersistentEdgeResize(instance);

    instance.dibujando = false;
    instance.map.getCanvas().style.cursor = '';
  }

  // ════════════════════════════════════════════════════════════════════════════
  // UBICACIONES (POLÍGONOS)
  // ════════════════════════════════════════════════════════════════════════════

  public addUbicacionesPolygons(
    instance: MapboxMapInstance,
    index: number,
    ubicaciones: IUbicacion[] | undefined,
    seleccionadosGenerales: SeleccionadosBarraLateral[] | undefined,
    defaultColor: string,
  ) {
    if (!ubicaciones || ubicaciones.length === 0) {
      const source = instance.map.getSource(
        instance.ubicacionesSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const selectionForInstance = seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    const mostrarUbicaciones =
      selectionForInstance?.mostrarUbicaciones ?? false;

    if (!mostrarUbicaciones) {
      const source = instance.map.getSource(
        instance.ubicacionesSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      const labelLayer = `${instance.ubicacionesSrc}-label`;
      if (instance.map.getLayer(labelLayer)) {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'none');
      }
      return;
    }

    const features: GeoJSON.Feature[] = [];

    for (const ubicacion of ubicaciones) {
      if (!ubicacion.geojson) continue;
      if (
        !this.serviceGeneral.shouldShowUbicacion(
          ubicacion,
          index,
          selectionForInstance,
        )
      ) {
        continue;
      }

      if (
        ubicacion.geojson.type === 'Polygon' &&
        ubicacion.geojson.coordinates
      ) {
        const feature: GeoJSON.Feature = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: ubicacion.geojson.coordinates as any,
          },
          properties: {
            id: ubicacion._id,
            nombre: ubicacion.identificacion,
            tipo: 'ubicacion',
            color: ubicacion.color || defaultColor,
          },
        };
        features.push(feature);
      }
    }

    const source = instance.map.getSource(
      instance.ubicacionesSrc,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }

    const mostrarNombreUbicaciones =
      selectionForInstance?.mostrarNombreUbicaciones ?? 'ninguno';
    const labelLayer = `${instance.ubicacionesSrc}-label`;
    if (instance.map.getLayer(labelLayer)) {
      if (mostrarNombreUbicaciones === 'todos') {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'visible');
      } else {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'none');
      }
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RECORRIDOS (LÍNEAS)
  // ════════════════════════════════════════════════════════════════════════════

  public addRecorridosLines(
    instance: MapboxMapInstance,
    index: number,
    recorridos: IRecorrido[] | undefined,
    seleccionadosGenerales: SeleccionadosBarraLateral[] | undefined,
    defaultColor: string,
  ) {
    if (!recorridos || recorridos.length === 0) {
      const source = instance.map.getSource(
        instance.recorridosSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const selectionForInstance = seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    const mostrarRecorridos =
      selectionForInstance?.mostrarRecorridos ?? 'ninguno';

    if (mostrarRecorridos === 'ninguno') {
      const source = instance.map.getSource(
        instance.recorridosSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      const labelLayer = `${instance.recorridosSrc}-label`;
      if (instance.map.getLayer(labelLayer)) {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'none');
      }
      return;
    }

    // En modo `seleccionado` sólo se dibuja el recorrido asignado al vehículo
    // que está abierto en la barra lateral. Si no hay vehículo seleccionado, o no
    // tiene recorrido, no se dibuja nada: es lo que se pidió, no todas las trazas.
    const idRecorridoDelSeleccionado =
      selectionForInstance?.reporteSeleccionado?.activo?.vehiculo
        ?.idRecorrido ?? selectionForInstance?.idRecorridoAsignado;

    const features: GeoJSON.Feature[] = [];

    for (const recorrido of recorridos) {
      if (!recorrido.geojson?.coordinates) continue;

      if (mostrarRecorridos === 'seleccionado') {
        if (
          !idRecorridoDelSeleccionado ||
          recorrido._id !== idRecorridoDelSeleccionado
        ) {
          continue;
        }
      } else if (
        // En modo `todos` siguen valiendo los filtros de cliente/recorrido del
        // panel de filtros.
        !this.serviceGeneral.shouldShowRecorrido(
          recorrido,
          index,
          selectionForInstance,
        )
      ) {
        continue;
      }

      if (recorrido.geojson.type === 'LineString') {
        const feature: GeoJSON.Feature = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: recorrido.geojson.coordinates as any,
          },
          properties: {
            id: recorrido._id,
            nombre: recorrido.nombre,
            color: recorrido.color || defaultColor,
            tipo: 'recorrido',
          },
        };
        features.push(feature);
      }
    }

    const source = instance.map.getSource(
      instance.recorridosSrc,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }

    const mostrarNombreRecorridos =
      selectionForInstance?.mostrarNombreRecorridos ?? 'ninguno';
    const labelLayer = `${instance.recorridosSrc}-label`;
    if (instance.map.getLayer(labelLayer)) {
      if (mostrarNombreRecorridos === 'todos') {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'visible');
      } else {
        instance.map.setLayoutProperty(labelLayer, 'visibility', 'none');
      }
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CUMPLIMIENTO DEL RECORRIDO ASIGNADO
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * Pinta la traza del recorrido asignado al vehículo seleccionado, partida en
   * los tramos que ya cubrió (sólido, más gruesa) y los que le faltan (gris
   * punteado).
   *
   * Es independiente del toggle "mostrar recorridos": ese muestra todas las
   * trazas del cliente, esto muestra el avance del vehículo que está mirando el
   * operador. Si no hay medición en curso, la capa queda vacía.
   */
  public addCumplimientoRecorrido(
    instance: MapboxMapInstance,
    index: number,
    recorridos: IRecorrido[] | undefined,
    seleccionadosGenerales: SeleccionadosBarraLateral[] | undefined,
    defaultColor: string,
  ) {
    const source = instance.map.getSource(
      instance.cumplimientoSrc,
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;

    const seleccion = seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );
    const cumplimiento = seleccion?.cumplimientoRecorrido;
    const idRecorrido =
      cumplimiento?.idRecorrido ?? seleccion?.idRecorridoAsignado;
    const recorrido = recorridos?.find((r) => r._id === idRecorrido);

    const features = featuresCumplimientoRecorrido(
      recorrido,
      cumplimiento,
      defaultColor,
    );
    source.setData({ type: 'FeatureCollection', features });
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PARADAS DEL RECORRIDO DINÁMICO ASIGNADO
  // ════════════════════════════════════════════════════════════════════════════

  private static readonly RADIO_METROS_DEFAULT = 60;

  /**
   * Pinta un círculo (con el radio real de tolerancia) por cada parada del
   * recorrido dinámico asignado al vehículo seleccionado — para ver a qué
   * destinos tiene que ir y con qué tolerancia, no solo la línea. Si el
   * vehículo no tiene ninguno asignado, la capa queda vacía.
   */
  public addParadasRecorrido(
    instance: MapboxMapInstance,
    index: number,
    recorridosDinamicos: IRecorridoDinamico[] | undefined,
    seleccionadosGenerales: SeleccionadosBarraLateral[] | undefined,
  ) {
    const source = instance.map.getSource(
      instance.recorridoParadasSrc,
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;

    const seleccion = seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );
    const idRecorridoDinamico =
      seleccion?.reporteSeleccionado?.activo?.vehiculo?.idRecorridoDinamico ??
      seleccion?.idRecorridoDinamicoAsignado;
    const recorrido = recorridosDinamicos?.find(
      (r) => r._id === idRecorridoDinamico,
    );

    const features: GeoJSON.Feature[] = (recorrido?.paradas ?? [])
      .filter((p) => p.geojson?.coordinates)
      .map((parada, i) => {
        const radioMetros =
          parada.radioMetros ?? ServiceDibujarVehiculos.RADIO_METROS_DEFAULT;
        const circulo = turf.circle(
          parada.geojson!.coordinates as [number, number],
          radioMetros,
          { steps: 64, units: 'meters' },
        );
        return {
          type: 'Feature',
          geometry: circulo.geometry,
          properties: {
            numero: i + 1,
            nombre: parada.nombre,
            direccion: parada.direccion,
          },
        };
      });

    source.setData({ type: 'FeatureCollection', features });
  }

  // ════════════════════════════════════════════════════════════════════════════
  // POLYLINES DE BÚSQUEDA
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * @param opciones.encuadrar Encuadra el mapa sobre la traza dibujada. Se
   * apaga cuando la traza crece en vivo (viaje en curso): reencuadrar con cada
   * reporte que llega le saca el mapa de las manos al usuario.
   */
  /**
   * Movimiento que corre sobre la traza: una capa punteada encima, en el color
   * de movimiento, con el patrón de guiones desplazándose.
   *
   * Es la técnica de dash animado de Mapbox: `line-dasharray` no se puede
   * interpolar, así que se cicla por una lista de patrones. Sale barato —
   * cambia una propiedad de pintado por frame, no toca la geometría — y de paso
   * marca la dirección del viaje.
   */
  private aplicarMovimiento(
    instance: MapboxMapInstance,
    polylines: LineStringMapaVehiculos[],
    trazaDibujada: [number, number][],
  ): void {
    const traza = polylines.find((pl) => pl.trip && pl.animada);
    const capa = `${instance.polylinesSrc}-flujo`;
    const capaLinea = `${instance.polylinesSrc}-line`;
    const capaFlechasLinea = `${instance.polylinesSrc}-arrow`;
    const srcLinea = `${instance.polylinesSrc}-flujo-linea`;

    const ver = (id: string, visible: boolean) => {
      if (!instance.map.getLayer(id)) return;
      instance.map.setLayoutProperty(
        id,
        'visibility',
        visible ? 'visible' : 'none',
      );
    };

    if (!traza) {
      this.pararMovimiento(instance);
      ver(capa, false);
      // Sin movimiento vuelven las flechas repartidas a lo largo de la línea.
      ver(capaFlechasLinea, true);
      return;
    }

    this.acotarCapasALineas(instance, [capaLinea, capaFlechasLinea]);
    ver(capaFlechasLinea, false);

    const colorLinea = traza.color || COLOR_REPORTE;
    const colorMovimiento = traza.colorMovimiento || colorVivo(colorLinea);
    const ancho = traza.width || 3;

    // La traza va entera y en su propio source: el degradado se mide con
    // `line-progress`, que corre de 0 a 1 por feature. Sobre los pedazos de
    // color el patron se reiniciaba en cada empalme.
    const linea = this.unirTramos(trazaDibujada);

    if (!instance.map.getSource(srcLinea)) {
      instance.map.addSource(srcLinea, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    const sourceLinea = instance.map.getSource(
      srcLinea,
    ) as mapboxgl.GeoJSONSource;
    sourceLinea?.setData({
      type: 'FeatureCollection',
      features:
        linea.length > 1
          ? [
              {
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: linea },
                properties: {},
              },
            ]
          : [],
    });

    if (!instance.map.getLayer(capa)) {
      instance.map.addLayer({
        id: capa,
        type: 'line',
        source: srcLinea,
        paint: {
          'line-width': ancho,
          'line-color': colorMovimiento,
          'line-dasharray': PATRONES_FLUJO[0],
        },
      });
      instance.polylinesLayers.push(capa);
    } else {
      ver(capa, true);
      instance.map.setPaintProperty(capa, 'line-width', ancho);
      instance.map.setPaintProperty(capa, 'line-color', colorMovimiento);
      // Por si la capa quedo de una version anterior con degradado encima.
      instance.map.setPaintProperty(capa, 'line-gradient', undefined);
    }

    // Debajo de los puntos de reporte: la capa del movimiento se agrega al
    // final, o sea arriba de todo, y les pasaba por encima tapándolos.
    const capaPuntos = `${instance.pointsSrc}-circle`;
    if (instance.map.getLayer(capaPuntos)) {
      instance.map.moveLayer(capa, capaPuntos);
    }

    this.pararMovimiento(instance);
    if (linea.length < 2) return;

    // Fuera de la zona de Angular: es un loop por cuadro, no tiene por que
    // disparar deteccion de cambios.
    this.ngZone.runOutsideAngular(() => {
      let paso = -1;

      const animar = (tiempo: number) => {
        const siguiente = Math.floor(
          (tiempo / MS_POR_PASO_FLUJO) % PATRONES_FLUJO.length,
        );

        if (siguiente !== paso) {
          paso = siguiente;

          if (instance.map.getLayer(capa)) {
            instance.map.setPaintProperty(
              capa,
              'line-dasharray',
              PATRONES_FLUJO[paso],
            );
          }
        }

        instance.flujoFrame = requestAnimationFrame(animar);
      };

      instance.flujoFrame = requestAnimationFrame(animar);
    });
  }

  /** Pega los tramos en una sola linea, sin repetir el punto de union. */
  private unirTramos(coords: [number, number][]): [number, number][] {
    const linea: [number, number][] = [];

    for (const punto of coords) {
      const ultimo = linea[linea.length - 1];
      if (ultimo && ultimo[0] === punto[0] && ultimo[1] === punto[1]) continue;
      linea.push(punto);
    }

    return linea;
  }

  /**
   * Deja las capas de linea evaluando solo geometrias de linea.
   *
   * El source de polylines lleva ademas puntos (flechas), y un punto sin la
   * propiedad `dash` hace fallar la evaluacion de `line-dasharray` al armar el
   * tile: Mapbox tira "Cannot read properties of null (reading 'join')" y el
   * recorrido no se dibuja. Se aplica en caliente porque los mapas ya abiertos
   * no vuelven a pasar por el armado de capas.
   */
  private acotarCapasALineas(
    instance: MapboxMapInstance,
    capas: string[],
  ): void {
    for (const capa of capas) {
      if (!instance.map.getLayer(capa)) continue;
      instance.map.setFilter(capa, ['==', ['geometry-type'], 'LineString']);
    }
  }

  /** Corta el loop del movimiento, si hay uno corriendo. */
  private pararMovimiento(instance: MapboxMapInstance): void {
    if (instance.flujoFrame === undefined) return;
    cancelAnimationFrame(instance.flujoFrame);
    instance.flujoFrame = undefined;
  }

  /**
   * Parte la traza en pedazos y le pone un peso a cada uno.
   *
   * El peso es la densidad: cuantos otros pedazos del mismo recorrido tiene
   * encima. El color se reparte despues por peso acumulado, asi avanza rapido
   * donde la traza se pisa a si misma y casi no avanza en tramos aislados.
   *
   * Es la parte cara (la densidad es cuadratica en la cantidad de pedazos), por
   * eso se calcula una vez por dibujo y se guarda: repintar por cambio de vista
   * solo rehace los colores.
   */
  private pedazosConPeso(
    tramos: [number, number][][],
  ): { tramo: [number, number][]; medio: [number, number]; peso: number }[] {
    const largoTotal = tramos.reduce(
      (total, tramo) => total + this.largoDeTramo(tramo),
      0,
    );
    if (!largoTotal) return [];

    const pedazos = this.cortarEnPedazos(tramos, largoTotal);
    const pesos = this.pesosPorDensidad(pedazos);

    return pedazos.map((pedazo, i) => ({
      tramo: pedazo.puntos,
      medio: pedazo.medio,
      peso: pesos[i],
    }));
  }

  /**
   * Reparte la rampa de color entre los pedazos, por peso acumulado.
   *
   * Con `limites` se reparte solo entre los pedazos que entran en pantalla: si
   * estas mirando un fragmento del recorrido, ese fragmento se lleva la rampa
   * entera y sus vueltas se distinguen aunque el viaje completo sea mucho mas
   * largo. Los pedazos de afuera toman el color del extremo que les toca.
   *
   * El precio es que el color pasa a depender de la vista: el mismo tramo
   * cambia de color al panear, y el color de arranque deja de significar "aca
   * empezo el viaje".
   */
  private colorearPedazos(
    pedazos: {
      tramo: [number, number][];
      medio: [number, number];
      peso: number;
    }[],
    colorInicio: string,
    colorFin: string,
    modo: 'degradado' | 'espectro',
    limites?: mapboxgl.LngLatBounds,
  ): { tramo: [number, number][]; color: string }[] {
    const seVe = (pedazo: { medio: [number, number] }) =>
      !limites || limites.contains(pedazo.medio);

    const pesoTotal = pedazos
      .filter(seVe)
      .reduce((total, pedazo) => total + pedazo.peso, 0);

    const colorDe = (t: number) =>
      modo === 'espectro'
        ? colorDelEspectro(colorFin, t)
        : mezclarColores(colorInicio, colorFin, t);

    let acumulado = 0;

    return pedazos.map((pedazo, i) => {
      if (seVe(pedazo)) acumulado += pedazo.peso;

      return {
        tramo: pedazo.tramo,
        color: colorDe(
          pesoTotal ? acumulado / pesoTotal : i / (pedazos.length || 1),
        ),
      };
    });
  }

  /**
   * Parte la traza en pedazos del color de la velocidad que llevaba el
   * vehículo en cada uno (ver `ESCALA_VELOCIDAD`), en degradado.
   *
   * Cada reporte se ubica sobre la traza dibujada igual que para la línea de
   * tiempo del reproductor, y la velocidad se interpola entre los dos reportes
   * que rodean a cada punto, como la hora: es la misma que muestra el tooltip
   * al pasar el mouse, así el color y el número no se contradicen.
   *
   * Para que el degradado se vea también donde la traza tiene pocos vértices
   * (uniendo reportes con rectas), los segmentos largos se subdividen. Y para
   * no llenar el mapa de features, el color se redondea a una cantidad fija de
   * escalones y los pedazos seguidos del mismo escalón se juntan en uno.
   *
   * Con `relativa`, la escala va de la mínima a la máxima del viaje.
   */
  private pedazosPorVelocidad(
    tramos: [number, number][][],
    puntos: PointLineStringVehiculo[],
    colorSinDatos: string,
    relativa: boolean,
  ): { tramo: [number, number][]; color: string }[] {
    const reportes = puntos
      .map((p) => ({
        coord: p.coordinate,
        t: Date.parse(p.fechaDevice),
        v: p.velocidad,
      }))
      .filter((p) => Number.isFinite(p.t));
    const coords = tramos.flat();
    if (!reportes.length || coords.length < 2) {
      return tramos.map((tramo) => ({ tramo, color: colorSinDatos }));
    }

    const { hastaVertice, distancias } = ubicarReportesEnTraza(
      coords,
      reportes,
      (a, b) => this.calculateDistance(a, b),
    );
    const ubicados = reportes.map((r, i) => ({ d: distancias[i], v: r.v }));

    const rango = relativa
      ? rangoVelocidad(reportes.map((r) => r.v))
      : undefined;
    // Redondeo al escalón: en la escala fija, de a 1 km/h; en la relativa, en
    // la misma cantidad de escalones repartidos entre la mínima y la máxima.
    const escalonar = (v: number): number => {
      if (!rango) return Math.round(v);
      const ancho = rango.max - rango.min;
      if (ancho <= 0) return rango.min;
      const t = (v - rango.min) / ancho;
      return (
        rango.min +
        (Math.round(t * ESCALONES_VELOCIDAD) / ESCALONES_VELOCIDAD) * ancho
      );
    };

    const largoTotal = hastaVertice[hastaVertice.length - 1];
    const paso = Math.max(
      METROS_MINIMOS_PASO_VELOCIDAD,
      largoTotal / MAX_PASOS_VELOCIDAD,
    );

    const pedazos: { tramo: [number, number][]; color: string }[] = [];
    // Índice, dentro de la traza completa, del primer vértice del tramo.
    let inicio = 0;
    for (const tramo of tramos) {
      let actual: { tramo: [number, number][]; color: string } | undefined;
      for (let j = 1; j < tramo.length; j++) {
        const [lngDesde, latDesde] = tramo[j - 1];
        const [lngHasta, latHasta] = tramo[j];
        const dDesde = hastaVertice[inicio + j - 1];
        const largo = hastaVertice[inicio + j] - dDesde;
        const partes = Math.max(1, Math.ceil(largo / paso));

        let anterior = tramo[j - 1];
        for (let k = 1; k <= partes; k++) {
          const f = k / partes;
          const punto: [number, number] =
            k === partes
              ? tramo[j]
              : [
                  lngDesde + f * (lngHasta - lngDesde),
                  latDesde + f * (latHasta - latDesde),
                ];
          const medio = dDesde + ((k - 0.5) / partes) * largo;
          const velocidad = valorInterpolado(ubicados, medio) ?? 0;
          const color = colorDeVelocidad(escalonar(velocidad), rango);

          if (actual?.color === color) {
            actual.tramo.push(punto);
          } else {
            actual = { tramo: [anterior, punto], color };
            pedazos.push(actual);
          }
          anterior = punto;
        }
      }
      inicio += tramo.length;
    }

    return pedazos;
  }

  /**
   * Repinta la traza con la rampa repartida sobre lo que se ve ahora. Reusa lo
   * guardado en el dibujo anterior: no rehace geometria ni densidad.
   */
  private repintarPorVista(instance: MapboxMapInstance): void {
    const guardado = instance.trazaPorVista;
    if (!guardado?.pedazos.length) return;

    const source = instance.map.getSource(
      instance.polylinesSrc,
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;

    const pedazos = this.colorearPedazos(
      guardado.pedazos,
      guardado.colorInicio,
      guardado.colorFin,
      'degradado',
      instance.map.getBounds() ?? undefined,
    );

    source.setData({
      type: 'FeatureCollection',
      features: [
        ...guardado.otros,
        ...pedazos.map((pedazo) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: pedazo.tramo,
          },
          properties: { ...guardado.propiedades, color: pedazo.color },
        })),
      ],
    });
  }

  /**
   * Engancha (una sola vez por mapa) el repintado al fin de cada movimiento.
   *
   * Va en `moveend` y no en `move`: repintar en cada cuadro del paneo es
   * rehacer la coleccion entera decenas de veces por segundo, y el color
   * cambiando mientras se arrastra el mapa marea.
   */
  private escucharVista(instance: MapboxMapInstance): void {
    this.ajustarPunteadoPorZoom(instance);

    if (instance.escuchaVista) return;
    instance.escuchaVista = true;

    instance.map.on('moveend', () => {
      this.repintarPorVista(instance);
      this.ajustarPunteadoPorZoom(instance);
    });
  }

  /**
   * Muestra u oculta la traza y los puntos de reporte, según lo elegido en el
   * panel.
   *
   * Es visibilidad de capa y no un filtro de features: la traza sigue estando,
   * con sus flechas y su movimiento, y volver a mostrarla no cuesta redibujar.
   */
  private aplicarVisibilidadRecorrido(
    instance: MapboxMapInstance,
    viaje: LineStringMapaVehiculos | undefined,
  ): void {
    if (!viaje) return;

    const ver = (capa: string, visible: boolean) => {
      if (!instance.map.getLayer(capa)) return;
      instance.map.setLayoutProperty(
        capa,
        'visibility',
        visible ? 'visible' : 'none',
      );
    };

    const verTraza = viaje.verTraza ?? true;
    for (const capa of [
      `${instance.polylinesSrc}-line`,
      `${instance.polylinesSrc}-arrow`,
      `${instance.polylinesSrc}-flujo`,
    ]) {
      // El movimiento tiene su propia condición: si está apagado, sigue oculto
      // aunque la traza se vea.
      if (capa.endsWith('-flujo') && !viaje.animada) continue;
      ver(capa, verTraza);
    }

    ver(`${instance.pointsSrc}-circle`, viaje.verReportes ?? true);
  }

  /**
   * Cambia el tamaño de los puntos de reporte.
   *
   * Va por `setPaintProperty` y no por una propiedad del feature para no tener
   * que reemitir la colección: el tamaño se toca con una barra y así el cambio
   * se ve mientras se arrastra.
   */
  private ajustarTamanoReportes(
    instance: MapboxMapInstance,
    radio: number | undefined,
  ): void {
    const capa = `${instance.pointsSrc}-circle`;
    if (!instance.map.getLayer(capa) || !radio) return;

    instance.map.setPaintProperty(capa, 'circle-radius', radio);
    // El borde acompaña al radio: con un punto grande, 1 px de borde
    // desaparece.
    instance.map.setPaintProperty(
      capa,
      'circle-stroke-width',
      Math.max(1, Math.round(radio / 5)),
    );
  }

  /**
   * Aplica el patrón de punteado a la capa de la traza, más tupido cuanto más
   * lejos se mira.
   *
   * `line-dasharray` se mide en anchos de línea sobre la PANTALLA, así que el
   * patrón siempre ocupa lo mismo en píxeles: de lejos el recorrido entero mide
   * poco y entran pocos guiones, que se ven perdidos. Achicando guión y hueco
   * entran más y la traza se sigue leyendo punteada.
   *
   * Va por código y no sólo en la definición de la capa porque los mapas ya
   * abiertos no vuelven a pasar por el armado: así el patrón agarra sin
   * recargar.
   */
  private ajustarPunteadoPorZoom(instance: MapboxMapInstance): void {
    const capa = `${instance.polylinesSrc}-line`;
    if (!instance.map.getLayer(capa)) return;

    const zoom = instance.map.getZoom();
    const punteado =
      zoom < 11 ? [0.5, 0.7] : zoom < 14 ? [0.7, 1] : PUNTEADO_BASE;

    instance.map.setPaintProperty(capa, 'line-dasharray', [
      'case',
      ['get', 'dash'],
      ['literal', punteado],
      ['literal', [1, 0]],
    ]);
  }

  /**
   * Corta la traza en pedazos de largo parejo. Por distancia y no por cantidad
   * de puntos: una traza tiene los puntos apelotonados donde el vehículo fue
   * lento, y por índice los pedazos quedaban desbalanceados.
   */
  private cortarEnPedazos(
    tramos: [number, number][][],
    largoTotal: number,
  ): { puntos: [number, number][]; medio: [number, number] }[] {
    // Pedazos cortos dan mejor resolución para detectar solapamientos, pero son
    // features de más: se acotan por largo mínimo y por cantidad.
    const largoPorPedazo = Math.max(
      largoTotal / MAX_PEDAZOS_DEGRADADO,
      METROS_MINIMOS_POR_PEDAZO,
    );

    const pedazos: { puntos: [number, number][]; medio: [number, number] }[] =
      [];

    const cerrar = (puntos: [number, number][]) => {
      if (puntos.length < 2) return;
      const medio = puntos[Math.floor(puntos.length / 2)];
      pedazos.push({ puntos, medio });
    };

    for (const tramo of tramos) {
      let actual: [number, number][] = [tramo[0]];
      let acumulado = 0;

      for (let i = 1; i < tramo.length; i++) {
        actual.push(tramo[i]);
        acumulado += this.calculateDistance(tramo[i - 1], tramo[i]);

        // Se corta repitiendo el punto del corte, para que no queden huecos
        // entre un pedazo y el siguiente.
        if (acumulado >= largoPorPedazo && i < tramo.length - 1) {
          cerrar(actual);
          actual = [tramo[i]];
          acumulado = 0;
        }
      }

      cerrar(actual);
    }

    return pedazos;
  }

  /**
   * Cuánto degradado consume cada pedazo. Un pedazo con vecinos encima (otra
   * pasada por la misma calle) pesa mucho; uno aislado, casi nada.
   *
   * Los vecinos inmediatos a lo largo de la traza no cuentan: son el pedazo
   * anterior y el siguiente, siempre están cerca y no son un solapamiento.
   */
  private pesosPorDensidad(
    pedazos: { puntos: [number, number][]; medio: [number, number] }[],
  ): number[] {
    return pedazos.map((pedazo, i) => {
      let vecinos = 0;

      for (let j = 0; j < pedazos.length; j++) {
        if (Math.abs(i - j) <= PEDAZOS_VECINOS_IGNORADOS) continue;
        if (
          this.calculateDistance(pedazo.medio, pedazos[j].medio) <=
          METROS_CERCANIA_PEDAZOS
        ) {
          vecinos++;
          if (vecinos >= MAX_VECINOS_CONTADOS) break;
        }
      }

      return PESO_PEDAZO_AISLADO + vecinos;
    });
  }

  private largoDeTramo(tramo: [number, number][]): number {
    let largo = 0;
    for (let i = 1; i < tramo.length; i++) {
      largo += this.calculateDistance(tramo[i - 1], tramo[i]);
    }
    return largo;
  }

  /**
   * Corre la traza `METROS_DESPLAZAMIENTO_TRAZA` metros hacia un costado,
   * siempre el mismo respecto de la dirección de avance, y la devuelve partida
   * en tramos (uno solo si no hay giros en U).
   *
   * Se hace a mano y no con `turf.lineOffset` porque ese justamente falla en el
   * caso que interesa: ante un giro en U, o ante puntos repetidos, devuelve
   * `null` o NaN y la traza terminaba dibujada en 0,0.
   */
  private desplazarTraza(coords: [number, number][]): [number, number][][] {
    const limpias = this.limpiarCoords(coords);
    if (limpias.length < 2) return [coords];

    const perpendiculares = limpias
      .slice(0, -1)
      .map((punto, i) => this.perpendicularDerecha(punto, limpias[i + 1]));

    const desplazamiento = METROS_DESPLAZAMIENTO_TRAZA / METROS_POR_GRADO;
    const tramos: [number, number][][] = [];
    let tramo: [number, number][] = [];

    for (let i = 0; i < limpias.length; i++) {
      const entrante = perpendiculares[i - 1] ?? null;
      const saliente = perpendiculares[i] ?? null;
      if (!entrante && !saliente) continue;

      // En un giro en U las dos perpendiculares son opuestas y su promedio se
      // anula (es lo que hacía fallar a turf). Se corta la traza ahí: cada
      // mitad sigue por su lado y queda un pequeño conector en el giro.
      const giroEnU =
        !!entrante &&
        !!saliente &&
        entrante[0] * saliente[0] + entrante[1] * saliente[1] < -0.5;

      let direccion: [number, number];
      if (!entrante) direccion = saliente!;
      else if (!saliente || giroEnU) direccion = entrante;
      else direccion = [entrante[0] + saliente[0], entrante[1] + saliente[1]];

      const largo = Math.hypot(direccion[0], direccion[1]);
      if (!largo) continue;

      const lat = (limpias[i][1] * Math.PI) / 180;
      const punto: [number, number] = [
        limpias[i][0] +
          ((direccion[0] / largo) * desplazamiento) / Math.cos(lat),
        limpias[i][1] + (direccion[1] / largo) * desplazamiento,
      ];
      if (!this.esCoordenadaValida(punto)) continue;

      tramo.push(punto);

      if (giroEnU) {
        if (tramo.length > 1) tramos.push(tramo);
        tramo = [punto];
      }
    }

    if (tramo.length > 1) tramos.push(tramo);
    return tramos.length ? tramos : [coords];
  }

  /**
   * Perpendicular unitaria a la derecha del avance, en un espacio donde la
   * longitud se achata por la latitud (si no, un mismo valor en grados mide
   * distinto en cada eje y la traza sale corrida en diagonal).
   */
  private perpendicularDerecha(
    a: [number, number],
    b: [number, number],
  ): [number, number] | null {
    const lat = (((a[1] + b[1]) / 2) * Math.PI) / 180;
    const dx = (b[0] - a[0]) * Math.cos(lat);
    const dy = b[1] - a[1];
    const largo = Math.hypot(dx, dy);
    if (!largo) return null;
    return [dy / largo, -dx / largo];
  }

  /**
   * Descarta coordenadas inválidas y puntos consecutivos repetidos (vehículo
   * detenido reportando la misma posición), que dejan segmentos de largo cero
   * sin perpendicular definida.
   */
  private limpiarCoords(coords: [number, number][]): [number, number][] {
    const limpias: [number, number][] = [];
    for (const punto of coords) {
      if (!this.esCoordenadaValida(punto)) continue;
      const ultima = limpias[limpias.length - 1];
      if (ultima && ultima[0] === punto[0] && ultima[1] === punto[1]) continue;
      limpias.push([punto[0], punto[1]]);
    }
    return limpias;
  }

  /** Descarta NaN, infinitos y el 0,0 que aparece cuando algo salió mal. */
  private esCoordenadaValida(punto: [number, number] | undefined): boolean {
    if (!punto || punto.length < 2) return false;
    const [lng, lat] = punto;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    if (lng === 0 && lat === 0) return false;
    return Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
  }

  public addPolylinesFromLineStringMapa(
    instance: MapboxMapInstance,
    polylines: LineStringMapaVehiculos[],
    defaultAccentColor: string,
    opciones?: { encuadrar?: boolean },
  ) {
    if (!polylines || polylines.length === 0) {
      const source = instance.map.getSource(
        instance.polylinesSrc,
      ) as mapboxgl.GeoJSONSource;
      const pointsSource = instance.map.getSource(
        instance.pointsSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      if (pointsSource) {
        pointsSource.setData({ type: 'FeatureCollection', features: [] });
      }
      // El movimiento vive en su propio source y capa: sin esto, al limpiar la
      // búsqueda los bloques de color quedaban dibujados sobre el mapa.
      try {
        this.aplicarMovimiento(instance, [], []);
      } catch (error) {
        console.warn('[Dibujar] No se pudo apagar el movimiento:', error);
      }

      const flujoSource = instance.map.getSource(
        `${instance.polylinesSrc}-flujo-linea`,
      ) as mapboxgl.GeoJSONSource | undefined;
      flujoSource?.setData({ type: 'FeatureCollection', features: [] });

      // Sin esto, el próximo movimiento del mapa repintaba la traza vieja.
      instance.trazaPorVista = undefined;
      instance.tripCoords = undefined;
      instance.tripPuntosFecha = undefined;
      instance.tripLineaTiempo = undefined;
      instance.tripFechaInicio = undefined;
      instance.tripFechaFin = undefined;

      if (instance.tripSrc) {
        const tripSource = instance.map.getSource(
          instance.tripSrc,
        ) as mapboxgl.GeoJSONSource;
        if (tripSource) {
          tripSource.setData({ type: 'FeatureCollection', features: [] });
        }
      }
      if (instance.tripMarkerSrc) {
        const tripMarkerSource = instance.map.getSource(
          instance.tripMarkerSrc,
        ) as mapboxgl.GeoJSONSource;
        if (tripMarkerSource) {
          tripMarkerSource.setData({
            type: 'FeatureCollection',
            features: [],
          });
        }
      }

      return;
    }

    const features: GeoJSON.Feature[] = [];
    const pointFeatures: GeoJSON.Feature[] = [];
    // La traza del viaje tal como queda dibujada (ya desplazada): es la que
    // sigue el movimiento, no la original.
    let trazaDibujada: [number, number][] = [];
    let trazaPorVista: MapboxMapInstance['trazaPorVista'];
    const allPoints: [number, number][] = [];
    let anyTrip = false;

    for (const pl of polylines) {
      const coordsParaDibujar = pl.pointsRecorrido || [];
      if (coordsParaDibujar.length === 0) continue;

      // Sólo la traza del viaje se corre: las polylines de visitas comparten
      // esta capa y no tienen el problema de la ida y la vuelta. Los giros en U
      // parten la traza en tramos, cada uno con su propio feature.
      const tramos =
        pl.trip && (pl.desplazar ?? true)
          ? this.desplazarTraza(coordsParaDibujar)
          : [coordsParaDibujar];
      const coords = tramos.flat();
      allPoints.push(...coords);
      if (pl.trip) trazaDibujada = coords;

      const colorBase = pl.color || defaultAccentColor;
      const propiedadesBase = {
        width: pl.width || 3,
        dash: pl.dash || false,
        tipo: 'polyline',
        // La capa del movimiento pinta sólo la traza del viaje, no las
        // polylines de visitas que comparten este source.
        esTraza: !!pl.trip,
      };

      // Cuando el color cambia a lo largo del viaje, viaja en los datos y no en
      // el pintado: la traza se parte en pedazos y cada uno lleva el suyo. Así
      // las flechas, que son símbolos sobre esta misma geometría y leen `color`
      // del feature, cambian junto con la línea (con `line-gradient` se
      // quedaban siempre de un solo color).
      // Por velocidad el color no es una rampa a lo largo del viaje: sale de
      // los reportes, así que va por su propio camino.
      const porVelocidad = !!pl.trip && pl.modoColor === 'velocidad';
      const partir =
        !!pl.trip &&
        !!pl.modoColor &&
        pl.modoColor !== 'plano' &&
        !porVelocidad;

      // Sin color elegido, el complementario: es el que mas contrasta. El panel
      // lo puede pisar porque el complementario de un rojo o un verde da pares
      // muy estridentes.
      const colorInicio =
        pl.colorDegradadoInicio || colorComplementario(colorBase);

      // En degradado la rampa se reparte sobre lo que entra en pantalla, asi un
      // fragmento del recorrido se lleva el degradado entero y sus vueltas se
      // distinguen. En espectro se reparte sobre el viaje completo.
      const conPeso = partir ? this.pedazosConPeso(tramos) : [];

      const pedazos = porVelocidad
        ? this.pedazosPorVelocidad(
            tramos,
            pl.pointsInfo ?? [],
            colorBase,
            !!pl.velocidadRelativa,
          )
        : partir
          ? this.colorearPedazos(
              conPeso,
              colorInicio,
              colorBase,
              pl.modoColor === 'espectro' ? 'espectro' : 'degradado',
              pl.modoColor === 'degradado'
                ? (instance.map.getBounds() ?? undefined)
                : undefined,
            )
          : tramos.map((tramo) => ({ tramo, color: colorBase }));

      if (pl.modoColor === 'degradado' && conPeso.length) {
        trazaPorVista = {
          pedazos: conPeso,
          otros: [],
          propiedades: propiedadesBase,
          colorInicio,
          colorFin: colorBase,
        };
      }

      for (const pedazo of pedazos) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: pedazo.tramo },
          properties: { ...propiedadesBase, color: pedazo.color },
        });
      }

      if (pl.pointsInfo && pl.pointsInfo.length > 0) {
        for (const pointInfo of pl.pointsInfo) {
          const coord = pointInfo.coordinate;
          const pointFeature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: coord },
            properties: {
              // El punto del reporte no es la traza: va en su propio color
              // para no leerse como parte de ella.
              color: pl.colorPuntos || COLOR_REPORTE,
              velocidad: pointInfo.velocidad,
              fechaDevice: pointInfo.fechaDevice,
              tipo: 'punto-recorrido',
              pointData: JSON.stringify({
                velocidad: pointInfo.velocidad,
                fechaDevice: pointInfo.fechaDevice,
              }),
            },
          };
          pointFeatures.push(pointFeature);
        }
      }

      if (pl.trip) {
        anyTrip = true;
        instance.tripCoords = coords;
        // La hora de cada lugar del viaje sale de los reportes, no de la traza
        // dibujada. La línea de tiempo se arma recién cuando hace falta.
        instance.tripPuntosFecha = (pl.pointsInfo ?? [])
          .map((p) => ({
            coord: p.coordinate,
            t: Date.parse(p.fechaDevice),
            v: p.velocidad,
          }))
          .filter((p) => Number.isFinite(p.t));
        instance.tripLineaTiempo = undefined;

        if (coords.length >= 2) {
          const startCoord = coords[0];
          const endCoord = coords[coords.length - 1];

          pointFeatures.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: startCoord },
            properties: {
              tipo: 'trip-start',
              color: '#4CAF50',
              icon: 'play_arrow',
            },
          });

          pointFeatures.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: endCoord },
            properties: {
              tipo: 'trip-end',
              color: '#F44336',
              icon: 'stop',
            },
          });
        }
      }
    }

    if (!anyTrip) {
      instance.tripCoords = undefined;
      instance.tripPuntosFecha = undefined;
      instance.tripLineaTiempo = undefined;
      instance.tripFechaInicio = undefined;
      instance.tripFechaFin = undefined;

      if (instance.tripSrc) {
        const tripSource = instance.map.getSource(
          instance.tripSrc,
        ) as mapboxgl.GeoJSONSource;
        if (tripSource) {
          tripSource.setData({ type: 'FeatureCollection', features: [] });
        }
      }
      if (instance.tripMarkerSrc) {
        const tripMarkerSource = instance.map.getSource(
          instance.tripMarkerSrc,
        ) as mapboxgl.GeoJSONSource;
        if (tripMarkerSource) {
          tripMarkerSource.setData({
            type: 'FeatureCollection',
            features: [],
          });
        }
      }
    }

    const source = instance.map.getSource(
      instance.polylinesSrc,
    ) as mapboxgl.GeoJSONSource;
    const pointsSource = instance.map.getSource(
      instance.pointsSrc,
    ) as mapboxgl.GeoJSONSource;

    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }

    if (trazaPorVista) {
      // Todo lo que no es la traza se reusa tal cual al repintar.
      trazaPorVista.otros = features.filter(
        (f) =>
          !(f.properties?.['esTraza'] && f.geometry?.type === 'LineString'),
      );
    }
    instance.trazaPorVista = trazaPorVista;
    this.escucharVista(instance);

    try {
      this.aplicarMovimiento(instance, polylines, trazaDibujada);
    } catch (error) {
      // El movimiento es un adorno: si falla, el recorrido tiene que quedar
      // dibujado igual.
      console.warn('[Dibujar] No se pudo aplicar el movimiento:', error);
    }

    if (pointsSource) {
      pointsSource.setData({
        type: 'FeatureCollection',
        features: pointFeatures,
      });
    }

    const viaje = polylines.find((pl) => pl.trip);
    this.ajustarTamanoReportes(instance, viaje?.radioPuntos);
    this.aplicarVisibilidadRecorrido(instance, viaje);

    if (allPoints.length > 0 && opciones?.encuadrar !== false) {
      const bounds = allPoints.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(allPoints[0], allPoints[0]),
      );
      instance.map.fitBounds(bounds, { padding: 50, maxZoom: 17 });
    }

    if (instance.polylinesVisible === undefined) {
      instance.polylinesVisible = true;
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PARADAS
  // ════════════════════════════════════════════════════════════════════════════

  public addParadas(
    instance: MapboxMapInstance,
    paradas: Stop[],
    skipFitBounds: boolean = false,
    esMapaActivo: boolean = true,
  ) {
    if (!paradas || paradas.length === 0) {
      const source = instance.map.getSource(
        instance.paradasSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const features: GeoJSON.Feature[] = [];

    for (const parada of paradas) {
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parada.longitude, parada.latitude],
        },
        properties: {
          id: parada.startTime,
          tipo: 'parada',
          color: '#29B6F6',
          paradaData: JSON.stringify(parada),
        },
      };
      features.push(feature);
    }

    const source = instance.map.getSource(
      instance.paradasSrc,
    ) as mapboxgl.GeoJSONSource;

    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    } else {
      console.error('❌ [addParadas] Source NO EXISTE:', instance.paradasSrc);
    }

    if (!skipFitBounds && paradas.length > 0 && esMapaActivo) {
      const bounds = paradas.reduce(
        (bounds, parada) => bounds.extend([parada.longitude, parada.latitude]),
        new mapboxgl.LngLatBounds(
          [paradas[0].longitude, paradas[0].latitude],
          [paradas[0].longitude, paradas[0].latitude],
        ),
      );
      instance.map.fitBounds(bounds, { padding: 50, maxZoom: 17 });
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EVENTOS
  // ════════════════════════════════════════════════════════════════════════════

  public addEventos(
    instance: MapboxMapInstance,
    eventos: (
      | IEventoGenerico
      | { longitude: number; latitude: number; id?: string }
    )[],
  ) {
    if (!eventos || eventos.length === 0) {
      const source = instance.map.getSource(
        instance.eventosSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const features: GeoJSON.Feature[] = eventos.map((evento) => {
      const isEventoGenerico = 'valores' in evento && 'fechaCreacion' in evento;
      const longitude = isEventoGenerico
        ? (((evento as IEventoGenerico).valores as IValoresEventoTracker)
            ?.geojson?.coordinates?.[0] ?? 0)
        : (evento as any).longitude;
      const latitude = isEventoGenerico
        ? (((evento as IEventoGenerico).valores as IValoresEventoTracker)
            ?.geojson?.coordinates?.[1] ?? 0)
        : (evento as any).latitude;
      const id = isEventoGenerico
        ? (evento as IEventoGenerico).fechaCreacion
        : (evento as any).id;

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [longitude, latitude],
        },
        properties: {
          id: id || '',
          tipo: 'evento',
          eventoData: isEventoGenerico ? JSON.stringify(evento) : undefined,
        },
      };
    });

    const source = instance.map.getSource(
      instance.eventosSrc,
    ) as mapboxgl.GeoJSONSource;

    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    } else {
      console.error('❌ [addEventos] Source NO EXISTE:', instance.eventosSrc);
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // OVERLAP RESOLUTION
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * Desplaza radialmente puntos de distintos tipos que coinciden en la misma
   * coordenada (dentro de `thresholdMeters`).
   * Solo aplica offset cuando hay al menos dos tipos distintos en el mismo lugar.
   * Dirección fija por tipo (triángulo equilátero): parada ↑ exceso ↙ evento ↘
   */
  public resolveOverlapOffsets(
    groups: {
      parada: [number, number][];
      exceso: [number, number][];
      evento: [number, number][];
    },
    thresholdMeters: number = 30,
  ): {
    parada: [number, number][];
    exceso: [number, number][];
    evento: [number, number][];
  } {
    type TypeKey = 'parada' | 'exceso' | 'evento';
    // Prioridad de orden visual dentro de un grupo: parada al frente, luego exceso, luego evento
    const typePriority: Record<TypeKey, number> = {
      parada: 0,
      exceso: 1,
      evento: 2,
    };
    const offsetMeters = 2;

    const all: { coord: [number, number]; type: TypeKey; idx: number }[] = [
      ...groups.parada.map((c, i) => ({
        coord: c,
        type: 'parada' as TypeKey,
        idx: i,
      })),
      ...groups.exceso.map((c, i) => ({
        coord: c,
        type: 'exceso' as TypeKey,
        idx: i,
      })),
      ...groups.evento.map((c, i) => ({
        coord: c,
        type: 'evento' as TypeKey,
        idx: i,
      })),
    ];

    const result: {
      parada: [number, number][];
      exceso: [number, number][];
      evento: [number, number][];
    } = {
      parada: groups.parada.map((c) => [c[0], c[1]]),
      exceso: groups.exceso.map((c) => [c[0], c[1]]),
      evento: groups.evento.map((c) => [c[0], c[1]]),
    };

    const processed = new Set<string>();

    for (let i = 0; i < all.length; i++) {
      const keyI = `${all[i].type}_${all[i].idx}`;
      if (processed.has(keyI)) continue;

      // Agrupar TODOS los puntos (mismo o distinto tipo) dentro del umbral
      const group = [all[i]];
      for (let j = i + 1; j < all.length; j++) {
        if (
          this.calculateDistance(all[i].coord, all[j].coord) < thresholdMeters
        ) {
          group.push(all[j]);
        }
      }

      group.forEach((p) => processed.add(`${p.type}_${p.idx}`));

      // Un solo punto en el grupo: no desplazar
      if (group.length <= 1) continue;

      // Ordenar por tipo (parada < exceso < evento) y luego por índice original
      // para que el orden sea determinista entre renders
      const sorted = [...group].sort((a, b) => {
        const ta = typePriority[a.type] ?? 3;
        const tb = typePriority[b.type] ?? 3;
        return ta !== tb ? ta - tb : a.idx - b.idx;
      });

      // Distribuir equitativamente en un círculo (360° / N)
      sorted.forEach((point, sortedIdx) => {
        const angleDeg = (360 / sorted.length) * sortedIdx;
        const angleRad = (angleDeg * Math.PI) / 180;
        const latRad = (point.coord[1] * Math.PI) / 180;
        const dx =
          ((offsetMeters / 1000) * Math.cos(angleRad)) /
          (111 * Math.cos(latRad));
        const dy = ((offsetMeters / 1000) * Math.sin(angleRad)) / 111;
        result[point.type][point.idx] = [
          point.coord[0] + dx,
          point.coord[1] + dy,
        ];
      });
    }

    return result;
  }
}
